import {
  Link,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import { getFormattedStatsCount, handleErrorMessage } from "helpers/methods";
import { LinkActivityType } from "models/interfaces";
import { useEffect, useState } from "react";
import { getLinkActivity } from "screens/Campaigns/CampaignService";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

interface CustomProps {
  id: number;
  maxRows?: number; // Optional prop to limit rows
  showTotalClicks?: boolean;
}

export const LinkActivityTableHeader = () => {
  return [
    {
      name: "Rank",
      field: "rank",
      align: "left",
      sorting: false,
      width: "100px",
    },
    {
      name: "Link",
      field: "link",
      align: "left",
      sorting: false,
    },
    {
      name: "Clicks",
      field: "clicks",
      align: "center",
      sorting: false,
    },
  ];
};

const LinkActivity = (props: CustomProps) => {
  const showTotalClicks = props.showTotalClicks ?? false;
  const [linksData, setLinksData] = useState<LinkActivityType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    if (props.id) {
      getLinkActivityData();
    }
  }, [props.id]);

  const convertDataToTableFormat = (data: any) => {
    return data.map((item: LinkActivityType, index: number) => {
      return {
        rank: getFormattedStatsCount(index + 1),
        link: (
          <Link href={item.url}>
            <a href={item.url} target="_blank">
              <Stack direction="row" spacing={1} alignItems="center">
                <CustomInfoTooltip infoMessage={item.url} placement="bottom">
                  <Typography
                    variant="body2"
                    noWrap
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxWidth="600px"
                    display="block"
                  >
                    {item.url}
                  </Typography>
                </CustomInfoTooltip>
                <OpenInNewIcon fontSize="small" sx={{ fontSize: "14px" }} />
              </Stack>
            </a>
          </Link>
        ),
        clicks: getFormattedStatsCount(item.count),
      };
    });
  };

  const getLinkActivityData = async () => {
    try {
      setLoading(true);
      const response = await getLinkActivity(props?.id);
      const tableData = convertDataToTableFormat(response);
      setTotal(
        response.reduce(
          (sum: number, current: LinkActivityType) => sum + current.count,
          0
        )
      );
      const limitedData = props.maxRows
        ? tableData.slice(0, props.maxRows)
        : tableData;
      setLinksData([...limitedData]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  return (
    <CustomTable
      isLoading={loading}
      headers={LinkActivityTableHeader()}
      rows={linksData}
      tableFooter={
        showTotalClicks && linksData.length > 0 ? (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                textAlign: "left",
                backgroundColor: "#FFF",
                borderBottom: "none",
              }}
            >
              <Typography variant="h6" fontSize={"14px !important"}>
                Total Clicks
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                backgroundColor: "#FFF",
                borderBottom: "none",
              }}
            >
              {total}
            </TableCell>
          </TableRow>
        ) : null
      }
      showPagination={false}
    />
  );
};

export default LinkActivity;
